import debounce from 'lodash.debounce';
import React from 'react';

import getCsrfToken from '../../../../utils/app/getCsrfToken';
import { bugsnag } from '../../../../utils/platform';
import { connectWebsocket } from '../../actions/websocket';
import onsiteNotificationCollection from '../../models/onsite-notification-collection';
import urls from '../../urls';
import OnsiteNotificationsDropdown from './components/OnsiteNotificationsDropdown/OnsiteNotificationsDropdown';
import OnsiteNotificationsIcon from './components/OnsiteNotificationsIcon/OnsiteNotificationsIcon';
import OnsiteNotificationsList from './components/OnsiteNotificationsList/OnsiteNotificationsList';
import { Notification } from './components/OnsiteNotificationsListItem/OnsiteNotificationsListItem';

export interface Props {
    urls: typeof urls;
}

export interface State {
    isActive: boolean;
    isLoading: boolean;
    notifications: Notification[];
    unreadCount: number;
    viewportHeightDiff: number | null;
    webPushVapidPublicKey: string | null;
    showEnableBrowserNotificationsPrompt: boolean;
}

export default class OnsiteNotifications extends React.Component<Props, State> {
    outerNode = React.createRef<HTMLDivElement>();
    fetchNotificationsWithDebounceFunc: ReturnType<typeof debounce> | null = null;

    state: State = {
        isActive: false,
        isLoading: true,
        notifications: [],
        unreadCount: 0,
        viewportHeightDiff: null,
        webPushVapidPublicKey: null,
        showEnableBrowserNotificationsPrompt: false,
    };

    componentDidMount() {
        this.fetchNotificationsWithDebounceFunc = debounce(this.fetchNotifications, 1000, {
            leading: true,
            maxWait: 1500,
        });

        connectWebsocket(this.props.urls.notificationsWebsocketAuth, (data: any) => {
            if (data.type) {
                if (data.type === 'unread-counter') {
                    this.setState(() => ({ unreadCount: data.count }));
                }
            }
        }).catch((err: Error) => {
            bugsnag.notify(err.name, err.message);
        });
    }

    onDocumentClick = (e: MouseEvent) => {
        if (
            this.outerNode &&
            this.outerNode.current &&
            !this.outerNode.current.contains(e.target as HTMLDivElement)
        ) {
            this.deactivate();
        }
    };

    // Flag (pre-emptively) all notifications as read. We do not re-fetch from the server
    // as that would re-order/mutate the result set the user is viewing.
    // TODO handle api route failure
    onMarkAllRead = async () => {
        const readNotifications = this.state.notifications.map(notification =>
            Object.assign({}, notification, { isRead: true })
        );
        this.setState(() => ({ notifications: readNotifications }));

        return await fetch(this.props.urls.xhrMarkAllRead, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: `csrf_token=${getCsrfToken()}`,
            credentials: 'same-origin',
        }).catch((err: Error) => {
            bugsnag.notify(err.name, err.message);
        });
    };

    fetchNotifications() {
        this.setState({ isLoading: true });

        return fetch(this.props.urls.xhrFetchNotifications, {
            credentials: 'same-origin',
        })
            .then(response => {
                if (!response.ok) throw Error(response.statusText);
                return response;
            })
            .then((response: Response) =>
                Promise.resolve(response.json()).catch((err: Error) => {
                    bugsnag.notify(err.name, err.message, { response });
                    throw new Error('Globalnav failed to fetch a valid notifications array.');
                })
            )
            .then(responseBody =>
                Promise.all([
                    Promise.resolve(onsiteNotificationCollection.fromJSON(responseBody)).catch(
                        (err: Error) => {
                            bugsnag.notify(err.name, err.message, {
                                response: { body: responseBody },
                            });
                            throw new Error(
                                'Globalnav failed to fetch a valid notifications array.'
                            );
                        }
                    ),
                    Promise.resolve(responseBody['web_push_vapid_public_key']),
                    Promise.resolve(responseBody['show_enable_browser_notifications_prompt']),
                ])
            )
            .then(
                ([notifications, webPushVapidPublicKey, showEnableBrowserNotificationsPrompt]) => {
                    this.setState(() => ({
                        isLoading: false,
                        notifications,
                        webPushVapidPublicKey,
                        showEnableBrowserNotificationsPrompt,
                    }));
                }
            )
            .catch(err => {
                if (err.message === 'Globalnav failed to fetch a valid notifications array.') {
                    return;
                }
                bugsnag.notify(err.name, err.message);
            });
    }

    async markReadCounter() {
        return await fetch(this.props.urls.xhrMarkReadCounter, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `csrf_token=${getCsrfToken()}`,
            credentials: 'same-origin',
        });
    }

    handleIconClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        this.setState(prevState => ({
            isActive: !prevState.isActive,
            viewportHeightDiff: prevState.isActive ? null : prevState.viewportHeightDiff,
        }));

        this.markReadCounter().catch((err: Error) => {
            bugsnag.notify(err.name, err.message);
        });
    };

    handleIconMouseEnter = () => {
        if (!this.state.isActive && this.fetchNotificationsWithDebounceFunc) {
            this.fetchNotificationsWithDebounceFunc();
        }
    };

    deactivate() {
        this.setState(() => ({
            isActive: false,
            viewportHeightDiff: null,
        }));
    }

    onViewportHeightAdjustment = (viewportHeightDiff: number) => {
        this.setState({ viewportHeightDiff });
    };

    render() {
        return (
            <div className="onsite-notifications" ref={this.outerNode}>
                <React.Fragment>
                    <div className="hidden--smallest hidden--small hidden--medium">
                        <OnsiteNotificationsIcon
                            data-onsite-notifications-icon-desktop
                            urls={this.props.urls}
                            onClick={this.handleIconClick}
                            onMouseEnter={this.handleIconMouseEnter}
                            unreadCount={this.state.unreadCount}
                        />
                    </div>
                    <div className="hidden--normal hidden--large">
                        <OnsiteNotificationsIcon
                            data-onsite-notifications-icon-mobile
                            urls={this.props.urls}
                            unreadCount={this.state.unreadCount}
                        />
                    </div>
                </React.Fragment>
                {this.state.isActive ? (
                    <OnsiteNotificationsDropdown
                        onViewportHeightAdjustment={this.onViewportHeightAdjustment}
                        onDocumentClick={this.onDocumentClick}
                    >
                        <OnsiteNotificationsList
                            notifications={this.state.notifications}
                            urls={this.props.urls}
                            onMarkAllRead={this.onMarkAllRead}
                            viewportHeightDiff={this.state.viewportHeightDiff}
                            isLoading={this.state.isLoading}
                            webPushVapidPublicKey={this.state.webPushVapidPublicKey}
                            showEnableBrowserNotificationsPrompt={
                                this.state.showEnableBrowserNotificationsPrompt
                            }
                        />
                    </OnsiteNotificationsDropdown>
                ) : null}
            </div>
        );
    }
}
